import { Directive, ElementRef, HostListener } from '@angular/core';
import { NgControl } from '@angular/forms';

/**
 * Angular 12 directive equivalent to AngularJS 'onlyDigitsLeadingZero' directive
 * Restricts input to digits only and handles leading zeros
 */
@Directive({
    selector: '[appOnlyDigits]',
    standalone: false
})
export class OnlyDigitsDirective {

  constructor(
    private el: ElementRef,
    private ngControl: NgControl
  ) {}

  @HostListener('input', ['$event'])
  onInput(event: any): void {
    const input = event.target;
    const value = input.value;

    if (value) {
      const stripped = value.replace(/[^0-9\-]/g, '');
      const sanitized = stripped.charAt(0) === '-'
        ? '-' + stripped.slice(1).replace(/-/g, '')
        : stripped.replace(/-/g, '');

      if (sanitized !== value) {
        input.value = sanitized;

        if (this.ngControl && this.ngControl.control) {
          this.ngControl.control.setValue(sanitized, { emitEvent: false });
        }

        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(_event: Event): void {
    const event = _event as KeyboardEvent;
    if (['Delete', 'Backspace', 'Tab', 'Escape', 'Enter'].includes(event.key) ||
        (event.ctrlKey && ['a', 'A', 'c', 'C', 'v', 'V', 'x', 'X', 'z', 'Z'].includes(event.key)) ||
        ['Home', 'End', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) {
      return;
    }

    if (event.key === '-' || event.key === 'Subtract') {
      const input = this.el.nativeElement;
      if (input.selectionStart !== 0 || input.value.includes('-')) {
        event.preventDefault();
      }
      return;
    }

    if (event.key.length === 1 && (event.key < '0' || event.key > '9')) {
      event.preventDefault();
    }
  }

  @HostListener('paste', ['$event'])
  onPaste(_event: Event): void {
    const event = _event as ClipboardEvent;
    event.preventDefault();
    const pastedInput: string = event.clipboardData?.getData('text/plain') || '';
    const match = pastedInput.match(/^-?\d+/);
    const cleaned = match ? match[0] : '';

    if (cleaned) {
      this.el.nativeElement.value = cleaned;

      if (this.ngControl && this.ngControl.control) {
        this.ngControl.control.setValue(cleaned);
      }
    }
  }
}

/**
 * Angular 12 directive for alphanumeric input with string handling
 * Equivalent to AngularJS 'only-digits-string' directive
 */
@Directive({
    selector: '[appOnlyDigitsString]',
    standalone: false
})
export class OnlyDigitsStringDirective {

  constructor(
    private el: ElementRef,
    private ngControl: NgControl
  ) {}

  @HostListener('input', ['$event'])
  onInput(event: any): void {
    const input = event.target;
    const value = input.value;
    
    if (value) {
      // Allow digits and some special characters for postal codes
      const allowedChars = value.replace(/[^0-9A-Za-z\-\s]/g, '');
      
      if (allowedChars !== value) {
        input.value = allowedChars;
        
        if (this.ngControl && this.ngControl.control) {
          this.ngControl.control.setValue(allowedChars);
        }
        
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }
  }

  @HostListener('keypress', ['$event'])
  onKeyPress(_event: Event): void {
    const event = _event as KeyboardEvent;
    // Allow: backspace, delete, tab, escape, enter
    if (['Delete', 'Backspace', 'Tab', 'Escape', 'Enter'].includes(event.key) ||
        // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+Z
        (event.ctrlKey && ['a', 'A', 'c', 'C', 'v', 'V', 'x', 'X', 'z', 'Z'].includes(event.key)) ||
        // Allow: home, end, arrow keys
        ['Home', 'End', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) {
      return;
    }
    
    // Allow: numbers, letters, dash, space
    if (!((event.key >= '0' && event.key <= '9') || // numbers
          (event.key >= 'A' && event.key <= 'Z') || // uppercase letters
          (event.key >= 'a' && event.key <= 'z') || // lowercase letters
          event.key === '-' || // dash
          event.key === ' ')) { // space
      event.preventDefault();
    }
  }
}